import type { ReactNode } from 'react';
import { Phone, PhoneCall, PhoneOff, UserCheck, Clock, TrendingUp } from 'lucide-react';
import type { CallImpactResponse } from '../types';
import './KpiCards.css';

interface KpiCardsProps {
  data: CallImpactResponse;
}

interface KpiCard {
  label: string;
  value: number | string;
  icon: ReactNode;
  color: string;
}

export default function KpiCards({ data }: KpiCardsProps) {
  const calls = data?.calls || [];
  const totalCalls = data?.total_calls || 0;

  const completedCalls = calls.filter((c) => c.call_status === 'completed').length;
  const missedCalls = calls.filter((c) => c.call_status === 'missed').length;
  const interestedCalls = calls.filter((c) => c.is_interested === true).length;

  const qualifiedCalls = calls.filter(
    (c) => c.call_summary?.qualified === true
  ).length;

  const completedWithDuration = calls.filter(
    (c) => c.call_status === 'completed' && c.call_duration
  );
  const avgDuration =
    completedWithDuration.length > 0
      ? Math.round(
          completedWithDuration.reduce((sum, c) => sum + (c.call_duration ?? 0), 0) /
            completedWithDuration.length
        )
      : 0;

  const callsWithInterest = calls.filter(
    (c) => c.call_summary?.interest_percentage != null
  );
  const avgInterestPct =
    callsWithInterest.length > 0
      ? Math.round(
          callsWithInterest.reduce(
            (sum, c) => sum + (c.call_summary?.interest_percentage ?? 0),
            0
          ) / callsWithInterest.length
        )
      : 0;

  const cards: KpiCard[] = [
    {
      label: 'Total Calls',
      value: totalCalls,
      icon: <Phone size={22} />,
      color: '#2563eb',
    },
    {
      label: 'Completed',
      value: completedCalls,
      icon: <PhoneCall size={22} />,
      color: '#16a34a',
    },
    {
      label: 'Missed',
      value: missedCalls,
      icon: <PhoneOff size={22} />,
      color: '#dc2626',
    },
    {
      label: 'Interested',
      value: interestedCalls,
      icon: <TrendingUp size={22} />,
      color: '#7c3aed',
    },
    {
      label: 'Qualified',
      value: qualifiedCalls,
      icon: <UserCheck size={22} />,
      color: '#0891b2',
    },
    {
      label: 'Avg Duration',
      value: `${avgDuration}s`,
      icon: <Clock size={22} />,
      color: '#ea580c',
    },
    {
      label: 'Avg Interest',
      value: `${avgInterestPct}%`,
      icon: <TrendingUp size={22} />,
      color: '#2563eb',
    },
  ];

  return (
    <div className="kpi-grid">
      {cards.map((card) => (
        <div className="kpi-card" key={card.label}>
          <div className="kpi-icon" style={{ backgroundColor: card.color + '18', color: card.color }}>
            {card.icon}
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{card.value}</span>
            <span className="kpi-label">{card.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
